import { Badge, Popover } from "../../../shared";
import { ClockIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { formatPhoneNumber } from "../../../utils";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/access-control";

/**
 * Build display lines for a location address.
 * Keeps this resilient across older/newer API shapes (zipCode vs zipcode, etc).
 *
 * @param {object | undefined | null} address
 * @returns {{ street?: string, cityLine?: string }}
 */
function getAddressLines(address) {
  if (!address) return {};

  const street = address?.address || address?.street || address?.line1;
  const city = address?.city;
  const state = address?.state;
  const zip = address?.zipcode || address?.zipCode || address?.postalCode;

  const cityState = [city, state].filter(Boolean).join(", ");
  const cityLine = [cityState, zip].filter(Boolean).join(" ").trim();

  return {
    street: street ? String(street) : undefined,
    cityLine: cityLine || undefined,
  };
}

const LocationTableCell = ({ data }) => {
  const { hasPermission } = usePermissions();
  const canEditJobs = hasPermission(PERMISSIONS.JOBS_EDIT);
  if (!data) return "";

  const { street, cityLine } = getAddressLines(data.address);
  const receivingHours = data.receivingHours || data.address?.receivingHours;

  return (
    <div className="flex flex-col">
      <div className="font-bold text-base">{data.name}</div>
      {(street || cityLine) && (
        <div className="text-sm text-gray-600">
          {street && <div>{street}</div>}
          {cityLine && <div>{cityLine}</div>}
        </div>
      )}
      <div className="flex space-x-1">
        {canEditJobs && data.precall && (
          <div>
            <Popover
              className="w-43"
              position="top"
              trigger={<Badge>Precall</Badge>}
              offset={[-12, 0]}
            >
              {receivingHours?.open && (
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4" />
                  <span className="text-gray-900 font-semibold test-base mx-2">
                    {receivingHours?.open}-{receivingHours?.close}
                  </span>
                </div>
              )}
              {data.phone ? (
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4" />
                  <span className="text-gray-900 font-semibold test-base mx-2">
                    {formatPhoneNumber(data.phone)}
                  </span>
                </div>
              ) : (
                "Set # for location"
              )}
            </Popover>
          </div>
        )}
        {data.note && (
          <div>
            <Popover
              className="w-43"
              position="top"
              trigger={<Badge color="green">Note</Badge>}
              offset={[-12, 0]}
            >
              {data.note}
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationTableCell;
